import React, { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { toast } from 'react-semantic-toasts';
import moment from 'moment';

import * as S from './styles';
import { REPROCESS_VIDEO } from '../../../../core/graphql/test-sessions';
import { COMPANY_UPLOAD_STORAGE_TRANSCRIBE_ERROR } from '../uploads-transcribe-error/graphql';

const COLUMNS = {
  date: 'Date',
  workspaceName: 'Workspace Name',
  project: 'Project',
  uploadedBy: 'Uploaded By',
  fileName: 'File Name',
  reprocess: 'Reprocess',
};
const TOAST_TIME = 2200;

function UploadsTable({ data }) {
  const [currentReprocessSessionIds, setCurrentReprocessSessionIds] = useState([]);

  const [
    reprocessSession,
    { loading: isSendingToReprocess },
  ] = useMutation(REPROCESS_VIDEO, {
    update: (cache, { data: response }) => {
      const { reprocessVideo } = response;
      const { uploadsFailed } = cache.readQuery({
        query: COMPANY_UPLOAD_STORAGE_TRANSCRIBE_ERROR,
        variables: { status: 'transcribe_error' },
      });
      cache.writeQuery({
        query: COMPANY_UPLOAD_STORAGE_TRANSCRIBE_ERROR,
        data: {
          uploadsFailed: uploadsFailed.map((session) => {
            if (session.id === reprocessVideo.testSession.id) {
              return {
                ...session,
                status: 'processing',
              };
            }

            return session;
          }),
        },
      });
    },
  });

  const handleReprocessSession = useCallback(async (testSessionId) => {
    try {
      setCurrentReprocessSessionIds((state) => [...state, testSessionId]);
      const response = await reprocessSession({ variables: { testSessionId } });

      if (!response || response.error) throw new Error('There was an error reprocessing the session');

      toast({
        description: `The session ${testSessionId} was sent to reprocessing`,
        time: TOAST_TIME,
        type: 'success',
      });
    } catch (error) {
      toast({
        description: 'There was an error reprocessing the session',
        time: TOAST_TIME,
        type: 'error',
      });
    } finally {
      setCurrentReprocessSessionIds((state) => state.filter((id) => id !== testSessionId));
    }
  }, [reprocessSession, setCurrentReprocessSessionIds]);

  const renderHeaders = useCallback(() => Object.entries(COLUMNS).map(([key, value]) => (
    <S.HeaderColumn key={key}>
      <S.Text.Bold>{value}</S.Text.Bold>
    </S.HeaderColumn>
  )), []);

  const reprocessButton = useCallback((session) => {
    const { id: testSessionId, status } = session;
    const isLoading = isSendingToReprocess && currentReprocessSessionIds.includes(testSessionId);

    if (status === 'transcribe_error') {
      return (
        <S.ReprocessButton
          onClick={() => handleReprocessSession(testSessionId)}
          disabled={isLoading}
          loading={isLoading}
        >
          Reprocess
        </S.ReprocessButton>
      );
    }

    return undefined;
  }, [currentReprocessSessionIds, handleReprocessSession, isSendingToReprocess]);

  const tableBody = useMemo(() => data.map((record) => (
    <S.Row key={record.id}>
      <S.Column><S.Text>{moment(record.createdAt).format('MMMM DD YYYY')}</S.Text></S.Column>
      <S.Column><S.Text>{record.project.company.name}</S.Text></S.Column>
      <S.Column><S.Text>{record.project.name}</S.Text></S.Column>
      <S.Column><S.Text>{record.user.profile.firstName}</S.Text></S.Column>
      <S.Column><S.Text>{record.testSession.videoKey.split('/').pop()}</S.Text></S.Column>
      <S.Column><S.CellWrapper><S.Text>{reprocessButton(record.testSession)}</S.Text></S.CellWrapper></S.Column>
    </S.Row>
  )), [data, reprocessButton]);

  return (
    <S.Container>
      <S.Table basic="very">
        <S.Header>
          <S.Row>
            {renderHeaders()}
          </S.Row>
        </S.Header>

        <S.Body>
          {tableBody}
        </S.Body>
      </S.Table>
    </S.Container>
  );
}

UploadsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      companyName: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default UploadsTable;
